import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../../../../components/navbar.jsx';
import Footer from '../../../../components/footer.jsx';
import ReadOnlyInput from '../../../../components/ReadOnlyInput.jsx';
import Dropdown from '../../../../components/Dropdown.jsx';
import Table from '../../../../components/Table.jsx';
import Button from '../../../../components/Button.jsx';
import Checkbox from '../../../../components/Checkbox.jsx';
import styles from '../css/PackageScriptQuickView.module.css'
import overlayStyles from '../../../../css/components/overlaymodal-animations.module.css';
import { CompletedPopup } from '../../ToFollows/views/FollowUpSignatureFlow.jsx';
import { fetchScriptDetails } from '../../../../api/dispensary/script/processScriptApi.js';
import { createReverseStock } from '../../../../api/Inventory/ReverseStockApi.js';
import { markReturned } from '../../../../api/dispensary/script/returnsApi.js';
import { useAuth } from '../../../../context/AuthContext.jsx';

const PackageScriptQuickView = () => {
    const { patientId } = useParams();
    const navigate = useNavigate();
    const parsedPatientId = parseInt(patientId, 10);
    const location = useLocation();
    const paymentTypeFromState = location.state?.paymentType || 'Cash';

    const mode = location.state?.mode || 'default'; // 'returnInventory' to enable return flow
    const repeatId = location.state?.repeatId || null;
    const repeatNumber = location.state?.repeatNumber ?? null;

    const [patientData, setPatientData] = useState(null);
    const [patientName, setPatientName] = useState('');
    const [practitioner, setPractitioner] = useState('');
    const [pharmacist, setPharmacist] = useState('');
    const [scriptMeta, setScriptMeta] = useState(null);

    const [medications, setMedications] = useState([]);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showCompleted, setShowCompleted] = useState(false);
    const { user } = useAuth();

    useEffect(() => {
        const load = async () => {
            try {
                const data = await fetchScriptDetails(parsedPatientId);
                if (!data) return;

                const script = data.script || {};
                setScriptMeta({
                    type: script.type || '',
                    processscriptnumber: script.processscriptnumber || '',
                    clinicreference: script.clinicreference || '',
                    paymentType: script.paymentType || paymentTypeFromState,
                    scriptType: (script.scriptType || '').toUpperCase() || 'PR',
                    // Signed script document URL resolved by backend (if any)
                    scriptDocUrl: script.docurl || null,
                });

                const patient = data.patient || {};
                const patientLabel = `${patient.surname || ''} ${patient.firstname || ''}`.trim();
                setPatientName(patientLabel);

                const profileFromDetails =
                    patient.profileNo ||
                    patient.profileno ||
                    patient.profileNumber ||
                    patient.profilenumber ||
                    null;

                const addrFromDetails = [];
                if (patient.homeStreet) addrFromDetails.push(patient.homeStreet);
                if (patient.homeSuburb) addrFromDetails.push(patient.homeSuburb);
                if (patient.homeCity) addrFromDetails.push(patient.homeCity);
                if (patient.homePostalCode) addrFromDetails.push(patient.homePostalCode);
                if (!addrFromDetails.length && patient.homeaddress) {
                    addrFromDetails.push(String(patient.homeaddress));
                }
                // Some APIs return a single combined address field
                if (!addrFromDetails.length && patient.address) {
                    addrFromDetails.push(String(patient.address));
                }

                const phoneFromDetails =
                    patient.contactNumber ||
                    patient.contactnumber ||
                    patient.cell ||
                    patient.cellphone ||
                    patient.cellphoneNo ||
                    patient.mobile ||
                    patient.phone ||
                    patient.telephone ||
                    '';

                const medicalAidNumberFromDetails = data.medicalAid?.number || null;

                const patientIdFromDetails =
                    patient.id ||
                    patient.patientId ||
                    patient.patientid ||
                    patient.patientID ||
                    data.patientId ||
                    (data.script && (data.script.patientId || data.script.patientid)) ||
                    null;

                const nextPatientData = {
                    id: parsedPatientId,
                    patientId: patientIdFromDetails,
                    profileNo: profileFromDetails,
                    addressLines: addrFromDetails,
                    phone: phoneFromDetails,
                    medicalAidNumber: medicalAidNumberFromDetails,
                };

                console.log('[PackageScriptQuickView] fetched script details', {
                    parsedPatientId,
                    data,
                    patient,
                    nextPatientData,
                });

                setPatientData(nextPatientData);

                const pracLabel = data.practitioner ? `${data.practitioner.title || ''} ${data.practitioner.firstname || ''} ${data.practitioner.surname || ''}`.trim() : '';
                setPractitioner(pracLabel);
                const meds = (data.drugs || []).map(d => ({
                    prescriptionDrugId: d.prescriptionDrugId,
                    sku: d.sku,
                    drugName: d.drugName,
                    packSize: d.packSize,
                    icd10: d.icd10,
                    strength: d.strength,
                    qty: d.quantity,
                    nappiCode: d.nappiCode,
                    instructions: d.instructions,
                    costPrice: typeof d.costPrice === 'number' || (typeof d.costPrice === 'string' && !d.costPrice.startsWith('R')) ? `R${d.costPrice}` : (d.costPrice || ''),
                    checked: false,
                }));
                setMedications(meds);
            } catch (e) {
                console.error('Failed to load script details', e);
            }
        };
        if (Number.isFinite(parsedPatientId)) load();
    }, [parsedPatientId]);

    // Prefill pharmacist from logged-in user
    useEffect(() => {
        if (!user) return;
        const first = (user.firstname || user.name || '').toString().trim();
        const last = (user.surname || '').toString().trim();
        const full = [first, last].filter(Boolean).join(' ');
        if (full) setPharmacist(full);
    }, [user]);

    const pharmacists = pharmacist ? [pharmacist] : [];

    const columns = [
        { header: 'Drug Name', accessor: 'drugName' },
        { header: 'Pack Size', accessor: 'packSize' },
        { header: 'ICD-10', accessor: 'icd10' },
        { header: 'Strength', accessor: 'strength' },
        { header: 'Qty', accessor: 'qty' },
        { header: 'Nappi Code', accessor: 'nappiCode' },
        { header: 'Instructions', accessor: 'instructions' },
        { header: 'Cost Price', accessor: 'costPrice' },
        ...(mode === 'returnInventory' ? [] : [{
            header: 'Check',
            accessor: 'checked',
            render: (value, row, rowIndex) => (
                <Checkbox
                    id={`check-${rowIndex}`}
                    checked={value}
                    onChange={(e) => {
                        const newMedications = [...medications];
                        newMedications[rowIndex].checked = e.target.checked;
                        setMedications(newMedications);
                    }}
                    disabled={false}
                />
            ),
        }]),
    ];

    const handleProcess = () => {
        const selectedMedications = medications.filter(med => med.checked);
        if (mode === 'returnInventory') {

            // In return mode, show confirmation instead of processing script
            setShowConfirm(true);
            return;
        }
        if (selectedMedications.length > 0 && scriptMeta) {
            const { type, processscriptnumber, clinicreference, paymentType, scriptType, scriptDocUrl } = scriptMeta;

            const total = selectedMedications.reduce((sum, med) => {
                const raw = typeof med.costPrice === 'string' ? med.costPrice : String(med.costPrice ?? '0');
                const cleaned = raw.replace(/[^0-9.\-]/g, '');
                const n = parseFloat(cleaned);
                return sum + (Number.isFinite(n) ? n : 0);
            }, 0);

            console.log('[PackageScriptQuickView] navigating to package-script-view', {
                prescriptionId: parsedPatientId,
                scriptMeta,
                patientData,
                selectedMedications,
                total,
            });

            navigate('/customer/dispensary/package-script-view', {
                state: {
                    type: type || 'process',
                    repeatId,
                    repeatNumber,
                    prescriptionId: parsedPatientId,
                    scriptData: {
                        type: type || 'process',
                        processscriptnumber: processscriptnumber || '',
                        clinicreference: clinicreference || '',
                        scriptNumber: processscriptnumber || clinicreference || '',
                        patient: patientName,
                        practitioner: practitioner,
                        pharmacist: pharmacist,
                        repeatId,
                        repeatNumber,
                        drugs: selectedMedications,
                        total,
                        paymentType: paymentType || paymentTypeFromState,
                        scriptType: scriptType || 'PR',
                        // Script document URL for View button on package-script-view
                        scriptDocUrl: scriptDocUrl || null,

                        patientId: patientData?.patientId || null,
                        profileNo: patientData?.profileNo || null,
                        addressLines: patientData?.addressLines || [],
                        phone: patientData?.phone || '',
                        medicalAidNumber: patientData?.medicalAidNumber || null,
                    },
                },
            });
        } else {

            console.log('No medications selected');
        }
    };

    const handleConfirmReturn = async () => {
        try {
            const returnItems = medications
                .map((med) => ({
                    prescriptiondrugid: med.prescriptionDrugId,
                    quantityreturned: med.qty,
                }))
                .filter((item) => Number.isFinite(Number(item.prescriptiondrugid)) && Number(item.quantityreturned) > 0);

            if (returnItems.length > 0) {
                await createReverseStock({
                    prescriptionid: parsedPatientId,
                    items: returnItems,
                    reason: 'Dispensary Return',
                });
            }

            await markReturned({
                prescriptionid: parsedPatientId,
                ...(repeatId ? { repeatid: repeatId } : {}),
            });
            setShowConfirm(false);

            setShowCompleted(true);
        } catch (e) {
            console.error('Failed to mark returned', e);
            setShowConfirm(false);
        }
    };

    const confirmDialog = (
        showConfirm && (
            <div className={`${overlayStyles.modalOverlay} ${overlayStyles.show}`}>
                <div
                    className={`${styles.modalContent} ${overlayStyles.stepEnter}`}
                >
                    <button className={overlayStyles.closeButton} onClick={() => setShowConfirm(false)} aria-label="Close">×</button>
                    <div className={styles.modalHeader}>
                        <div className={styles.warningIcon}>
                            !
                        </div>
                        <h3 className={styles.modalTitle}>Are you sure you want to return these items to inventory?</h3>
                        <div className={styles.modalActions}>
                            <Button variant="primary" onClick={handleConfirmReturn}>Return</Button>
                            <Button variant="secondary" onClick={() => setShowConfirm(false)}>Cancel</Button>
                        </div>
                    </div>
                </div>
            </div>
        )
    );

    if (!patientData) return <div>Loading...</div>;

    return (
        <div className={styles.container}>
            <Navbar backTo="/customer/dispensary/package-script" />
            <main className={styles.main}>
                <h1 className={styles.title}>Patient Medication Details</h1>
                <div className={styles.formContainer}>
                    <ReadOnlyInput
                        label="Patient Name"
                        value={patientName}
                    />
                    <ReadOnlyInput
                        label="Practitioner"
                        value={practitioner}
                    />
                    <ReadOnlyInput
                        label="Pharmacist"
                        value={pharmacist}
                    />
                </div>
                <div className={styles.tableContainer}>
                    <Table columns={columns} data={medications} />
                </div>
                <div className={styles.buttonContainer}>
                    <Button variant="primary" onClick={handleProcess}>
                        {mode === 'returnInventory' ? 'Return To Inventory' : 'Process'}
                    </Button>
                </div>
            </main>
            <Footer />
            {confirmDialog}
            <CompletedPopup isOpen={showCompleted} onClose={() => { setShowCompleted(false); navigate('/customer/dispensary/package-script'); }} />
        </div>
    );
};

export default PackageScriptQuickView;